import React, { FunctionComponent, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count, fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Dt,
  IconButton,
  Typography
} from '../../../../components/v2/styled';

import { QuestionnaireResult } from '../../../../models/QuestionnaireResult';
import { User } from '../../../../models/User';

// const { DateTime } = require("luxon");

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { CoachShowQuestionnaire } from '../../../../components/v2/dialogs/CoachShowQuestionnaire';

type Props = {
  object: User;
  setPremiumQuestionnaireOpen: (b:boolean) => void;
}

export const CoachUserWidgetsQuestionnairesSmall: FunctionComponent<Props> = ({
  object,
  setPremiumQuestionnaireOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  // The result opened in the view popup (CoachShowQuestionnaire). Set by the
  // per-row eye button; the fetch_one below loads its full body.
  const [activeQResult, setActiveQResult] = useState<number>();
  const [qResultOpen, setQResultOpen] = useState<boolean>(false);

  let qrCount = 0;
  let qrCountCompleted = 0;

  let is_questionnaire = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'questionnaire'});

  // Coach-level widget settings (the `cta` goto-button toggle and the `mode`
  // body picker, defaults on/stats). Stored on
  // login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.questionnairesSmallValueKey];
  const showCta = HelpersClientWidgets.getQuestionnairesSmallCtaEnabled(cfg);
  const mode = HelpersClientWidgets.getQuestionnairesSmallMode(cfg);

  // Count-only queries feeding the stats body. `d.count` is the total matching
  // the filter regardless of per_page. Only fetched in stats mode.
  const {
    // data: qrs,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["client", "questionnaire_results", object?.id],
    queryFn: () =>
      fetch_all_with_count<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire_result',
          fields: ['id'],
          page: 0,
          per_page: 1,
          order: "updated_at DESC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        qrCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!(login?.practice_id || currentPractice?.id) && !!object?.id && object?.type === "User::User",
  });
  const {
    // data: qrComps,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["client", "questionnaire_results_completed", object?.id],
    queryFn: () =>
      fetch_all_with_count<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire_result',
          fields: ['id'],
          page: 0,
          per_page: 1,
          order: "updated_at DESC",
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id),
              is_completed: 1
            }
          }
        },
        login
      ),
      select: (d) => {
        qrCountCompleted = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!(login?.practice_id || currentPractice?.id) && !!object?.id && object?.type === "User::User",
  });

  // Last-5 list body: the client's questionnaire results by updated_at
  // (bumps on invite, mail resend and completion alike — the closest
  // single-column proxy for "recent activity" the API offers), newest first.
  // Only fetched in list mode.
  const {
    data: lastResults,
    refetch: refetchLastResults,
  } = useQuery({
    queryKey: ["client", "questionnaire_results_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire_result',
          fields: ['id', 'invited_at', 'created_at', 'mail_sent_at', 'completed_at', 'questionnaire'],
          sub_fields: {
            questionnaire: ['id', 'name']
          },
          order: "updated_at DESC",
          per_page: 5,
          page: 0,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => d.result,
    enabled: mode === 'list' && !!(login?.practice_id || currentPractice?.id) && !!object?.id && object?.type === "User::User",
  });

  const lastFive = (lastResults || []).slice(0, 5);

  // Full result body for the view popup — same projection the questionnaires
  // page uses for its View dialog. Only fetched while a result is active.
  const {
    data: questionnaireResult,
  } = useQuery({
    queryKey: ["questionnaire_result", "widget", activeQResult],
    queryFn: () =>
      fetch_one<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire_result',
          id: activeQResult,
          fields: [
            'id', 'name', 'extra_data', 'questionnaire', 'practice', 'comment', 'is_locked', 'is_frontend'
          ],
          sub_fields: {
            'practice': ['id', 'name'],
            'questionnaire': ['id', 'name', 'extra_data', 'content_type']
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!activeQResult,
  });

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row
        (space-saving — the old fullwidth bottom button cost a full row).
        Gated by the coach's `cta` setting; the premium lock uses the v2
        Button's built-in outlined + star treatment. */}
    {showCta && <Button
      label={t("coach.general.actions.to_questionnaires")}
      id="goto_questionnaires"
      text
      size="small"
      premiumLocked={!is_questionnaire}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        if (is_questionnaire) {
          navigate(`/user/${object.id}/questionnaires`);
        } else {
          setPremiumQuestionnaireOpen(true);
        }
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.questionnaires")}</Typography>
        <Divider />
      </Grid>

      {/* Stats body: one i18n plural pair per metric (invited = all the
          user's results / completed), separated by a middot. Only shown when
          there's at least one result (matches the old title-count guard). */}
      {mode === 'stats' && qrCount > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t("coach.submenu.detail.questionnaires_invited_count", {count: qrCount})} · ${t("coach.submenu.detail.questionnaires_completed_count", {count: qrCountCompleted})}`}
        </Typography>
      </Grid>}

      {/* List body: last 5 questionnaire results (newest activity first), a
          Divider between consecutive rows. Row caption shows the completion
          when completed, otherwise the invite (falling back to created_at
          when the result was never invited) — mail activity only affects
          the ordering (via updated_at), never the caption. The trailing eye
          (right-aligned behind the title, same row pattern as the courses
          euro mark) opens the view popup — only rendered for completed
          results (nothing to view otherwise). */}
      {mode === 'list' && lastFive.map((result, idx) => <React.Fragment key={result.id}>
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" noWrap>
                {result.questionnaire?.name || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {(() => {
                const invitedDate = result.invited_at || result.created_at;
                return <>
                  {!!result.completed_at && <Typography variant="caption" sx={{color: 'text.secondary'}}>
                    {t("coach.models.questionnaire_results.completed_at")}: <Dt d={result.completed_at} f="DDD t" />
                  </Typography>}
                  {!result.completed_at && !!invitedDate && <Typography variant="caption" sx={{color: 'text.secondary'}}>
                    {t("coach.models.questionnaire_results.invited_at")}: <Dt d={invitedDate} f="DDD t" />
                  </Typography>}
                </>;
              })()}
            </Grid>
          </Grid>
          {!!result.completed_at && <IconButton
            title={"View"}
            sx={{padding: 0.5}}
            onClick={() => {
              setActiveQResult(result.id);
              setQResultOpen(true);
            }}
          >
            <VisibilityIcon sx={{fontSize: '1.1rem'}} />
          </IconButton>}
        </Grid>
        {idx < lastFive.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}
    </Grid>

    {/* View popup — same dialog component as the questionnaires page.
        `setSavedOpen` is a no-op toast hook; a save inside the dialog bumps
        updated_at, so the list refetches below keeps the order honest. */}
    {!!questionnaireResult && <CoachShowQuestionnaire
      open={qResultOpen}
      setOpen={setQResultOpen}
      object={questionnaireResult}
      setSavedOpen={(b) => {
        refetchLastResults();
      }}
    />}
  </Paper>;
}
